'use client';

import React, { useEffect, useId, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { getPerformanceLabLenis } from 'utils/performanceLabLenisRegistry';
import { PLCTA } from 'components/performance-lab/PLCTA';
import { useOpenCallbackModal } from 'components/performance-lab/hooks';
import { useScrollProgress } from './useFestiveScrollMotion';
import styles from './FestiveStepRows.module.css';

// The comms doc's four gifting steps — the Figma rows are placeholders (all "Choose a protocol").
const STEPS = [
  {
    titleKey: 'festive.howItWorks.steps.0.title',
    bodyKey: 'festive.howItWorks.steps.0.body',
  },
  {
    titleKey: 'festive.howItWorks.steps.1.title',
    bodyKey: 'festive.howItWorks.steps.1.body',
  },
  {
    titleKey: 'festive.howItWorks.steps.2.title',
    bodyKey: 'festive.howItWorks.steps.2.body',
  },
  {
    titleKey: 'festive.howItWorks.steps.3.title',
    bodyKey: 'festive.howItWorks.steps.3.body',
  },
] as const;

/** Each row starts rising when its top reaches 95% of the viewport and settles by 55%. */
const REVEAL_START = 0.95;
const REVEAL_END = 0.55;

/**
 * Scroll-linked, staggered reveal: writes `--reveal` (0 → 1) on every row. The
 * CSS gives later rows a longer travel, so the stack separates and settles
 * like parallax layers. Reverses on scroll up; static for reduced motion.
 */
const useStaggeredRowReveal = () => {
  const listRef = useRef<HTMLOListElement>(null);

  useEffect(() => {
    const list = listRef.current;
    // The list's own transform is never set, so its rect is a stable anchor.
    if (!list) return undefined;
    const rows = Array.from(list.children) as HTMLElement[];

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      rows.forEach((row) => row.style.setProperty('--reveal', '1'));
      return undefined;
    }

    let raf = 0;
    const update = () => {
      const vh = window.innerHeight;
      rows.forEach((row) => {
        // Measure the untransformed slot (offsets share an offsetParent) so the
        // row's own travel doesn't feed back into its progress.
        const top =
          list.getBoundingClientRect().top + (row.offsetTop - list.offsetTop);
        const progress =
          (vh * REVEAL_START - top) / (vh * (REVEAL_START - REVEAL_END));
        row.style.setProperty(
          '--reveal',
          String(Math.min(1, Math.max(0, progress))),
        );
      });
    };
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    getPerformanceLabLenis()?.on?.('scroll', onScroll);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      getPerformanceLabLenis()?.off?.('scroll', onScroll);
    };
  }, []);

  return listRef;
};

/**
 * V1b steps panel (Figma Performance Lab Web 5225:8353): heading, intro and an
 * outline "Book a callback" on the left; hairline rows with square corner
 * markers on the right, revealed one after another on scroll.
 */
/** Exposes the intro's height as `--intro-h` so CSS can pin it at the viewport centre. */
const useMeasuredHeight = <T extends HTMLElement>() => {
  const ref = useRef<T>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(([entry]) => {
      el.style.setProperty(
        '--intro-h',
        `${Math.round(entry.contentRect.height)}px`,
      );
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return ref;
};

/** `type="geist"`: V2 treatment (Figma 5324:2229) — Geist headings, Geist Mono labels, grain. */
export const FestiveStepRows: React.FC<{
  type?: 'geist';
  showCallback?: boolean;
}> = ({ type, showCallback = true }) => {
  const t = useTranslations('performance-lab');
  const openCallback = useOpenCallbackModal();
  const listRef = useStaggeredRowReveal();
  const introRef = useMeasuredHeight<HTMLDivElement>();
  // --scroll (−1 → 1 across the section) turns the corner squares as the visitor scrolls.
  const panelRef = useScrollProgress<HTMLElement>();
  const headingId = useId();
  // Only a tab stop while it actually scrolls sideways (the phone carousel).
  const [scrollable, setScrollable] = useState(false);

  useEffect(() => {
    const list = listRef.current;
    if (!list) return undefined;
    const measure = () => setScrollable(list.scrollWidth > list.clientWidth);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(list);
    return () => observer.disconnect();
  }, [listRef]);

  return (
    <section
      ref={panelRef}
      className={styles.panel}
      data-type={type}
      aria-label={t('festive.howItWorks.aria')}
    >
      <div className={styles.intro} ref={introRef}>
        <div className={styles.introCopy}>
          <h2 id={headingId} className={styles.heading}>
            {t('festive.versions.stepRows.heading')}
          </h2>
          <p className={styles.introBody}>
            {t(
              type === 'geist'
                ? 'festive.versions.v2.stepRows.intro'
                : 'festive.versions.stepRows.intro',
            )}
          </p>
        </div>
        {showCallback && (
          <PLCTA
            type="button"
            $plVariant="white"
            className={`${styles.callback} pl-custom-type`}
            onClick={() => openCallback({ flow: 'pl_festive_callback' })}
            data-buttontype="performance_lab_festive_steps_callback"
          >
            {t('festive.versions.hero.cta')}
          </PLCTA>
        )}
      </div>

      <ol
        className={styles.rows}
        ref={listRef}
        tabIndex={scrollable ? 0 : undefined}
        aria-labelledby={headingId}
      >
        {STEPS.map((step, index) => (
          <li
            className={styles.row}
            key={step.titleKey}
            style={{ '--row-index': index } as React.CSSProperties}
          >
            <span className={styles.rule} data-edge="top" aria-hidden="true" />
            <span
              className={styles.marker}
              data-corner="top-left"
              aria-hidden="true"
            />
            <span
              className={styles.marker}
              data-corner="top-right"
              aria-hidden="true"
            />
            <span className={styles.side} data-side="left" aria-hidden="true" />
            <span
              className={styles.side}
              data-side="right"
              aria-hidden="true"
            />
            {index === STEPS.length - 1 && (
              <>
                <span
                  className={styles.rule}
                  data-edge="bottom"
                  aria-hidden="true"
                />
                <span
                  className={styles.marker}
                  data-corner="bottom-left"
                  aria-hidden="true"
                />
                <span
                  className={styles.marker}
                  data-corner="bottom-right"
                  aria-hidden="true"
                />
              </>
            )}
            <span className={styles.label}>
              {t('festive.versions.steps.label', {
                number: String(index + 1).padStart(2, '0'),
              })}
            </span>
            <h3 className={styles.title}>
              {t(
                type === 'geist'
                  ? `festive.versions.v2.stepRows.steps.${index}.title`
                  : step.titleKey,
              )}
            </h3>
            <p className={styles.body}>
              {t(
                type === 'geist'
                  ? `festive.versions.v2.stepRows.steps.${index}.body`
                  : step.bodyKey,
              )}
            </p>
            {/* Spare height only below body so STEP / title / body share top baselines. */}
            <span className={styles.cardSpacer} aria-hidden="true" />
          </li>
        ))}
      </ol>
    </section>
  );
};
